/**
 * MarkdownWithCitations Component
 *
 * Renders markdown content with full formatting support (via MarkdownWeBuild)
 * while making citation markers like [C1], [C2] clickable.
 *
 * This component:
 * 1. Renders markdown using MarkdownWeBuild for proper formatting
 * 2. Post-processes the DOM to add click handlers to citation markers
 * 3. Preserves all markdown formatting (headings, lists, tables, etc.)
 */
"use client";

import React, { useRef, useEffect } from "react";

import { Citation } from "@/types/citations";
import { MarkdownWeBuild } from "@/components/MarkdownWeBuild";

interface MarkdownWithCitationsProps {
  message: string;
  citations: Citation[];
  onCitationClick?: (citation: Citation) => void;
}

/**
 * Component that renders full markdown with clickable citations
 */
export const MarkdownWithCitations: React.FC<MarkdownWithCitationsProps> = ({
  message,
  citations,
  onCitationClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !citations || citations.length === 0) return;

    const container = containerRef.current;

    // Find all text nodes and replace citation markers with clickable elements

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    const nodesToProcess: {
      // eslint-disable-next-line no-undef
      node: Text;
      citations: Array<{ citation: Citation; match: RegExpMatchArray }>;
    }[] = [];

    // First pass: collect all text nodes that contain citations
    // eslint-disable-next-line no-undef
    let currentNode = walker.nextNode() as Text;

    while (currentNode) {
      const text = currentNode.textContent || "";
      const citationMatches: Array<{ citation: Citation; match: RegExpMatchArray }> = [];

      // Check each citation against this text node
      citations.forEach((citation) => {
        // Escape special regex characters in citation label
        const escapedLabel = citation.label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`\\b${escapedLabel}\\b`, "g");
        let match;

        while ((match = regex.exec(text)) !== null) {
          citationMatches.push({ citation, match });
        }
      });

      if (citationMatches.length > 0) {
        nodesToProcess.push({ node: currentNode, citations: citationMatches });
      }

      // eslint-disable-next-line no-undef
      currentNode = walker.nextNode() as Text;
    }

    // Second pass: replace text nodes with elements containing clickable citations
    nodesToProcess.forEach(({ node, citations: citationMatches }) => {
      const text = node.textContent || "";
      const parent = node.parentNode;

      if (!parent) return;

      // Sort matches by position (descending) to replace from end to start
      const sortedMatches = citationMatches.sort(
        (a, b) => (b.match.index || 0) - (a.match.index || 0),
      );

      // Create a document fragment to hold the new nodes
      const fragment = document.createDocumentFragment();
      let lastIndex = text.length;

      // Process matches in reverse order
      sortedMatches.forEach(({ citation, match }) => {
        const matchStart = match.index || 0;
        const matchEnd = matchStart + match[0].length;

        // Add text after this citation
        if (lastIndex > matchEnd) {
          const textAfter = text.substring(matchEnd, lastIndex);

          fragment.insertBefore(document.createTextNode(textAfter), fragment.firstChild);
        }

        // Create clickable citation span
        const citationSpan = document.createElement("span");

        citationSpan.textContent = citation.label;
        citationSpan.className = "citation-link";
        citationSpan.style.color = "#0066cc";
        citationSpan.style.cursor = "pointer";
        citationSpan.style.textDecoration = "underline";
        citationSpan.style.fontWeight = "500";
        citationSpan.setAttribute("role", "button");
        citationSpan.setAttribute("tabindex", "0");
        citationSpan.setAttribute(
          "aria-label",
          `View citation ${citation.label}: ${citation.filename}`,
        );
        citationSpan.setAttribute("data-citation-label", citation.label);

        // Add click handler
        citationSpan.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onCitationClick) {
            onCitationClick(citation);
          }
        });

        // Add keyboard handler
        citationSpan.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            if (onCitationClick) {
              onCitationClick(citation);
            }
          }
        });

        fragment.insertBefore(citationSpan, fragment.firstChild);
        lastIndex = matchStart;
      });

      // Add text before first citation
      if (lastIndex > 0) {
        const textBefore = text.substring(0, lastIndex);

        fragment.insertBefore(document.createTextNode(textBefore), fragment.firstChild);
      }

      // Replace the text node with the fragment
      parent.replaceChild(fragment, node);
    });

    // Cleanup function to remove event listeners
    return () => {
      const citationLinks = container.querySelectorAll(".citation-link");

      citationLinks.forEach((link) => {
        const clone = link.cloneNode(true);

        link.parentNode?.replaceChild(clone, link);
      });
    };
  }, [message, citations, onCitationClick]);

  return (
    <div ref={containerRef}>
      <MarkdownWeBuild>{message}</MarkdownWeBuild>
    </div>
  );
};
